import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { TaskRecord, TaskCadence } from '@/types/assets';
import { useAssetsStore } from '@/stores/assetsStore';
import { toast } from 'sonner';
import dayjs from 'dayjs';
import { EntityLinkSelector, STANDARD_LINKABLE_TYPES } from '@/components/shared/EntityLinkSelector';
import { UserSelector } from '@/components/shared/UserSelector';
import { useLinkStore } from '@/stores/linkStore';
import type { EntityType } from '@/types/link';

const taskSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  cadence: z.enum(['once', 'daily', 'weekly', 'monthly', 'quarterly', 'yearly', 'custom_rrule']),
  rrule: z.string().optional(),
  owner_id: z.string().optional(),
  due_date: z.string().optional(),
  tags: z.string().optional(),
});

type TaskFormData = z.infer<typeof taskSchema>;

interface SelectedEntity {
  id: string;
  type: EntityType;
  name: string;
}

interface TaskDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nodeId: string;
  task?: TaskRecord;
  preLinkedEntity?: SelectedEntity;
}

// Exclude 'task' from allowed types (can't link task to itself)
const ALLOWED_LINK_TYPES = STANDARD_LINKABLE_TYPES.filter(t => t !== 'task');

export function TaskDialog({ open, onOpenChange, nodeId, task, preLinkedEntity }: TaskDialogProps) {
  const { addTask, updateTask } = useAssetsStore();
  const { bulkCreateLinks } = useLinkStore();
  const [selectedLinks, setSelectedLinks] = useState<SelectedEntity[]>([]);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<TaskFormData>({
    resolver: zodResolver(taskSchema),
    defaultValues: task ? {
      title: task.title,
      description: task.description,
      cadence: task.cadence,
      rrule: task.rrule,
      owner_id: task.owner,
      due_date: dayjs(task.due_next).format('YYYY-MM-DD'),
      tags: task.tags?.join(', '),
    } : {
      cadence: 'monthly',
      due_date: dayjs().add(1, 'month').format('YYYY-MM-DD'),
    },
  });

  // Pre-populate links when entity is provided
  useEffect(() => {
    if (preLinkedEntity && open) {
      setSelectedLinks([preLinkedEntity]);
    } else if (!open) {
      setSelectedLinks([]);
    }
  }, [preLinkedEntity, open]);

  const watchedCadence = watch('cadence');
  const watchedOwnerId = watch('owner_id');

  const onSubmit = async (data: TaskFormData) => {
    const dueNext = data.due_date 
      ? dayjs(data.due_date).toISOString()
      : getNextDueDate(data.cadence);

    const tags = data.tags 
      ? data.tags.split(',').map(tag => tag.trim()).filter(Boolean)
      : [];

    let taskId: string | undefined;

    if (task) {
      updateTask(task.id, {
        title: data.title,
        description: data.description,
        cadence: data.cadence,
        rrule: data.rrule,
        owner: data.owner_id,
        due_next: dueNext,
        tags,
      });
      taskId = task.id;
      toast.success('Task updated successfully');
    } else {
      // addTask returns void, we need to handle this differently
      addTask({
        nodeId,
        title: data.title,
        description: data.description,
        cadence: data.cadence,
        rrule: data.rrule,
        owner: data.owner_id,
        due_next: dueNext,
        status: 'Scheduled',
        tags,
      });
      toast.success('Task created successfully');
    }

    // Create links if task existed (for editing)
    if (taskId && selectedLinks.length > 0) {
      const linksByType = selectedLinks.reduce((acc, link) => {
        if (!acc[link.type]) acc[link.type] = [];
        acc[link.type].push(link.id);
        return acc;
      }, {} as Record<EntityType, string[]>);

      for (const [targetType, targetIds] of Object.entries(linksByType)) {
        await bulkCreateLinks('task', taskId, targetType as EntityType, targetIds);
      }
    }
    
    reset();
    setSelectedLinks([]);
    onOpenChange(false);
  };

  const getNextDueDate = (cadence: TaskCadence): string => {
    const now = dayjs();
    switch (cadence) {
      case 'daily':
        return now.add(1, 'day').toISOString();
      case 'weekly':
        return now.add(1, 'week').toISOString();
      case 'monthly':
        return now.add(1, 'month').toISOString();
      case 'quarterly':
        return now.add(3, 'month').toISOString();
      case 'yearly':
        return now.add(1, 'year').toISOString();
      default:
        return now.add(1, 'month').toISOString();
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{task ? 'Edit Task' : 'Create Recurring Task'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <Label htmlFor="title">Task Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter task title"
            />
            {errors.title && (
              <p className="text-sm text-danger">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Describe the task"
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cadence">Cadence *</Label>
              <Select
                value={watchedCadence}
                onValueChange={(value) => setValue('cadence', value as TaskCadence)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="once">Once</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="yearly">Yearly</SelectItem>
                  <SelectItem value="custom_rrule">Custom (RRULE)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="due_date">Next Due Date</Label>
              <Input
                id="due_date"
                type="date"
                {...register('due_date')}
              />
            </div>
          </div>

          {watchedCadence === 'custom_rrule' && (
            <div>
              <Label htmlFor="rrule">RRULE</Label>
              <Input
                id="rrule"
                {...register('rrule')}
                placeholder="FREQ=MONTHLY;INTERVAL=1"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter a valid RRULE string for custom recurrence patterns
              </p>
            </div>
          )}

          <UserSelector
            value={watchedOwnerId}
            onChange={(owner_id) => setValue('owner_id', owner_id)}
            label="Owner"
            placeholder="Select owner (optional)"
            allowUnassigned
          />

          <div>
            <Label htmlFor="tags">Tags</Label>
            <Input
              id="tags"
              {...register('tags')}
              placeholder="backup, patch, configuration (comma-separated)"
            />
          </div>

          <EntityLinkSelector
            allowedTypes={ALLOWED_LINK_TYPES}
            onLinksChange={setSelectedLinks}
            initialLinks={selectedLinks}
          />

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit">
              {task ? 'Update Task' : 'Create Task'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
